import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { ApiChatService } from '@providers/api/chat/chat.service';
import { assistantChatActions } from './chat.actions';
import { selectAssistantChatChannelChatQueryInfo } from './chat.selectors';
import { AssistantChatService } from './chat.service';

/**
 * Assistant Chat Effects.
 */
@Injectable()
export class AssistantChatEffects extends AssistantChatService {
  private readonly actions$ = inject(Actions);
  private readonly chatApi = inject(ApiChatService);
  private readonly store = inject(Store);

  readonly checkChannel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        assistantChatActions.checkChannel,
        AuthActions.login,
        AuthActions.switchProfile
      ),
      switchMap(() =>
        this.chatApi
          .getChannels({
            limit: 1
          })
          .pipe(
            map((response) =>
              assistantChatActions.checkChannelSuccess({
                result: Array.isArray(response.data) && response.data.length > 0
              })
            ),
            catchError((err: HttpErrorResponse) =>
              of(
                assistantChatActions.checkChannelFailed({
                  error: {
                    message: err.message,
                    status: err.status
                  }
                })
              )
            )
          )
      )
    );
  });

  readonly checkIsChannelHasChat$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        assistantChatActions.checkChannelHasChat,
        assistantChatActions.setCurrentChannelId
      ),
      withLatestFrom(
        this.store.select(selectAssistantChatChannelChatQueryInfo)
      ),
      switchMap((data) => {
        const state = data[1];
        return state.currentChannelId
          ? this.chatApi.getChats({
              limit: 1,
              channelId: [state.currentChannelId]
            })
          : of({
              count: 0,
              data: []
            });
      }),
      map((response) => {
        return assistantChatActions.checkChannelHasChatSuccess({
          result: response.data.length > 0
        });
      }),
      catchError(() => of(assistantChatActions.checkChannelHasChatFailed()))
    );
  });

  readonly fetchChannelChats$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(assistantChatActions.fetchChannelChats),
      withLatestFrom(
        this.store.select(selectAssistantChatChannelChatQueryInfo)
      ),
      switchMap(([action, state]) =>
        this.chatApi
          .getChats({
            ...state.currentQuery,
            limit: state.queryLimit,
            page: action.page,
            channelId: state.currentChannelId
              ? [state.currentChannelId]
              : undefined
          })
          .pipe(
            map((response) => {
              return assistantChatActions.fetchChannelChatsSuccess({
                page: action.page,
                items: Array.isArray(response.data) ? response.data : [],
                totalItems: response.count,
                channelId: state.currentChannelId,
                queryKey: state.currentQueryKey
              });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(
                assistantChatActions.fetchChannelChatsFailed({
                  error: {
                    status: error.status,
                    message: error.message
                  },
                  page: action.page,
                  channelId: state.currentChannelId,
                  queryKey: state.currentQueryKey
                })
              );
            })
          )
      )
    );
  });

  readonly fetchChannels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        assistantChatActions.fetchChatHistory,
        assistantChatActions.reFetchChatHistory
      ),
      switchMap(({ limit, page, query }) =>
        this.chatApi
          .getChannels({
            limit,
            page: page,
            ...query
          })
          .pipe(
            map((response) =>
              assistantChatActions.fetchChatHistorySuccess({
                limit,
                items: response.data,
                pagination: {
                  page,
                  totalItems: response.count
                }
              })
            ),
            catchError((err: HttpErrorResponse) =>
              of(
                assistantChatActions.fetchChatHistoryFailed({
                  error: {
                    message: err.message,
                    status: err.status
                  }
                })
              )
            )
          )
      )
    );
  });

  readonly resetOnAuthChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login, AuthActions.logout, AuthActions.switchProfile),
      map(() => assistantChatActions.resetAllUserData())
    );
  });

  readonly updateChat$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(assistantChatActions.updateChat),
      switchMap((payload) => {
        return this.chatApi
          .getChats({
            limit: 1,
            page: 0,
            channelId: [payload.channelId],
            id: [payload.chatId]
          })
          .pipe(
            map((response) =>
              assistantChatActions.updateChatSuccess({
                ...payload,
                chat: response.data.length > 0 ? response.data[0] : undefined
              })
            )
          );
      })
    );
  });
}
